import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { BoxSelectionBoxSize, TextFieldTheme, TextField as TPATextField } from 'wix-ui-tpa/cssVars';
import { useFieldAttributes } from '../../services/field-attributes';
import { FieldDescriptionWUT } from '../field-description/field-description-wut';
import { FieldErrorWUT } from '../field-error/field-error-wut';
import { FieldLabelWUT } from '../field-label/field-label-wut';
import Fieldset from '../fieldset';
import { classes } from './donation-field-wut.st.css';
import { OptionsLayout } from '../options-layout';
import { NUMBER_OF_COLUMNS } from '../options-layout/options-layout-constants';
import { debounce } from 'ts-debounce';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useTranslation } from '../../contexts/translation-context';
import { mapById } from '../../utils/map-by-field';
import { useConfig } from '../../contexts/config-context';
import { findProductWithDynamicPrice, getCustomAmount, getProductValueOnCheckboxChange, toProductValue } from './donation-field-utils';
import { getValidNumberInput } from '../shared/numeric-input-utils';
import { PRODUCT_PRICE_MULTIPLE_OF } from '../../field-settings/settings-shared/products/product-price';
import { DonationFieldOption } from './donation-field-option';
import { getCurrencyAriaLabel } from '../shared/payment-field-utils';
export const DonationFieldWUT = _ref => {
  let {
    id,
    target,
    label,
    hideLabel = false,
    options,
    addOther,
    addOtherPlaceholder,
    required = false,
    value = null,
    onChange,
    onFocus,
    onBlur,
    error,
    hasError = false,
    errorMessage,
    disabled = false,
    description,
    validation,
    additionalInfo,
    FieldLayout,
    numberOfColumns = NUMBER_OF_COLUMNS.ONE,
    requiredIndicatorType,
    requiredIndicatorPlacement
  } = _ref;
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy,
    prefixId
  } = useFieldAttributes(id, error, description);
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const {
    t
  } = useTranslation();
  const {
    currency,
    regionalFormat
  } = useConfig();
  const productValue = value == null ? void 0 : value[0];
  const products = useMemo(() => {
    var _validation$predefine;
    return (validation == null || (_validation$predefine = validation.predefined) == null || (_validation$predefine = _validation$predefine.paymentOptions) == null ? void 0 : _validation$predefine.products) || [];
  }, [validation]);
  const productsById = useMemo(() => mapById(products), [products]);
  const customAmountProduct = useMemo(() => addOther ? findProductWithDynamicPrice(products) : undefined, [products, addOther]);
  const [customAmount, setCustomAmount] = useState(getCustomAmount(productValue, customAmountProduct));
  const handleChange = useCallback(productId => {
    onChange(getProductValueOnCheckboxChange(productId, productValue, productsById));
    setCustomAmount('');
  }, [onChange, productsById, setCustomAmount, productValue]);
  const handleCustomAmountChange = useCallback(amount => {
    if (!customAmountProduct) {
      return;
    }
    if (typeof amount === 'number') {
      onChange([toProductValue(customAmountProduct, String(amount))]);
    } else {
      onChange([]);
    }
  }, [onChange, customAmountProduct]);
  const debouncedHandleCustomAmountChange = useMemo(() => {
    return debounce(handleCustomAmountChange, 500);
  }, [handleCustomAmountChange]);
  const handleCustomAmountInputChange = useCallback(event => {
    if ((productValue == null ? void 0 : productValue.productId) !== (customAmountProduct == null ? void 0 : customAmountProduct.id)) {
      onChange(undefined);
    }
    const {
      validInput,
      validNumber
    } = getValidNumberInput(event.target.value, PRODUCT_PRICE_MULTIPLE_OF, customAmount, regionalFormat);
    setCustomAmount(validInput);
    debouncedHandleCustomAmountChange(validNumber);
  }, [regionalFormat, onChange, productValue, customAmountProduct, customAmount, setCustomAmount, debouncedHandleCustomAmountChange]);
  return /*#__PURE__*/React.createElement(FieldLayout, {
    fieldId: id,
    renderLabel: () => /*#__PURE__*/React.createElement(FieldLabelWUT, {
      labelId: labelId,
      required: required,
      label: label,
      hideLabel: hideLabel,
      requiredIndicatorType: requiredIndicatorType,
      requiredIndicatorPlacement: requiredIndicatorPlacement
    }),
    renderInput: () => /*#__PURE__*/React.createElement(Fieldset, {
      id: inputId,
      ariaLabelledBy: labelId,
      className: classNames(classes.container),
      ariaDescribedBy: ariaDescribedBy,
      onFocus: onFocus,
      onBlur: onBlur,
      required: required
    }, options.length > 0 && /*#__PURE__*/React.createElement(OptionsLayout, {
      numberOfColumns: numberOfColumns,
      tabIndex: -1
    }, options.map((_ref2, index) => {
      var _productsById$optionV;
      let {
        value: optionValue
      } = _ref2;
      const isChecked = (productValue == null ? void 0 : productValue.productId) === optionValue;
      const price = (_productsById$optionV = productsById[optionValue]) == null || (_productsById$optionV = _productsById$optionV.fixedPriceOptions) == null ? void 0 : _productsById$optionV.price;
      return /*#__PURE__*/React.createElement(DonationFieldOption, {
        id: optionValue,
        name: target,
        size: BoxSelectionBoxSize.small,
        disabled: disabled,
        key: `donation-${optionValue}`,
        "data-hook": `donation-${price}`,
        checked: isChecked,
        onChange: event => {
          handleChange(event.id);
        },
        forcedHover: forcedHover,
        price: price,
        hasError: hasError,
        inputId: index === 0 ? id : undefined
      });
    })), addOther && /*#__PURE__*/React.createElement(TPATextField, {
      placeholder: addOtherPlaceholder === undefined ?
      // only show to users who added donation field before placeholder defaultValue was added
      t('donation.other-option.placeholder') : addOtherPlaceholder,
      "aria-labelledby": `${labelId} ${prefixId}`,
      className: classNames(classes.otherInput, classes.theme, {
        [classes.hover]: forcedHover,
        [classes.filled]: Boolean(customAmount)
      }),
      id: customAmountProduct == null ? void 0 : customAmountProduct.id,
      value: customAmount,
      name: target,
      onChange: handleCustomAmountInputChange,
      disabled: disabled,
      min: 0,
      "data-hook": "donation-custom-amount",
      inputMode: "decimal",
      theme: TextFieldTheme.Box,
      error: hasError,
      newErrorMessage: true,
      prefix: customAmount && currency && /*#__PURE__*/React.createElement("span", {
        id: prefixId,
        "aria-hidden": false,
        "aria-label": getCurrencyAriaLabel(t, currency)
      }, currency.sign || currency.code),
      autoComplete: "off"
    })),
    renderDescription: () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FieldErrorWUT, {
      target: target,
      hasError: hasError,
      errorMessage: errorMessage,
      id: errorId
    }), /*#__PURE__*/React.createElement(FieldDescriptionWUT, {
      description: description,
      id: descriptionId
    }), additionalInfo)
  });
};
//# sourceMappingURL=donation-field-wut.js.map